import React, { useEffect, useRef } from "react";

/**
 * WaveformStrip — Serato-style dual waveform display.
 * Purely visual: generates a pseudo-waveform based on a deterministic seed per
 * deck + a playhead that advances while the deck is playing. Does NOT touch
 * the audio element's MediaElementSource (which would redirect audio away
 * from default speakers and silence the DJ).
 */
export default function WaveformStrip({
  audioRefA, audioRefB,
  colorA = "#38bdf8", colorB = "#fb7185",
  playingA, playingB,
  tempoA = 0.5, tempoB = 0.5,    // 0..1 maps to ±8% playbackRate (used to scale beat grid)
  bpmA = 120, bpmB = 120,        // assumed BPM when real analysis isn't available
}) {
  const canvasA = useRef(null);
  const canvasB = useRef(null);
  const raf = useRef(0);

  const drawDeck = (canvas, color, active, offset, audioEl, tempo, bpm) => {
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.fillStyle = "#05070B";
    ctx.fillRect(0, 0, w, h);
    // Pseudo waveform bars
    const barW = 2;
    const gap = 1;
    const count = Math.ceil(w / (barW + gap));
    for (let i = 0; i < count; i++) {
      const x = i * (barW + gap);
      const seed = (i + offset) * 12.9898;
      const s = Math.abs(Math.sin(seed) * 43758.5453) % 1;
      const lowMid = Math.abs(Math.sin(seed * 0.25)) % 1;
      let amp = (0.25 + s * 0.6 + lowMid * 0.2) * (h * 0.45);
      if (!active) amp *= 0.15;
      ctx.fillStyle = color;
      ctx.fillRect(x, h / 2 - amp, barW, amp * 2);
    }
    // Beat grid overlay — ticks spaced by 1 beat of playback time, scaled to pixels
    // Scroll rate: the waveform advances `offset` pixels per tick; 1 tick = 33ms,
    // so pixels/sec ≈ offset/t which in practice here is 30px/s (offset++ every 33ms).
    if (bpm > 0) {
      const rate = 1 + (tempo - 0.5) * 0.16; // ±8%
      const pxPerSec = 30; // matches the 1px/frame @ ~30fps scroll
      const pxPerBeat = pxPerSec * (60 / (bpm * rate));
      const beatPhase = (offset % pxPerBeat + pxPerBeat) % pxPerBeat;
      // Draw downbeats (every 4 beats) stronger, regular beats thinner.
      ctx.globalAlpha = 0.45;
      for (let i = 0; ; i++) {
        const x = Math.floor(w - beatPhase - i * pxPerBeat);
        if (x < 0) break;
        const isDown = i % 4 === 0;
        ctx.fillStyle = isDown ? "#ffffff" : "rgba(255,255,255,0.45)";
        ctx.fillRect(x, isDown ? 0 : h * 0.2, 1, isDown ? h : h * 0.6);
      }
      ctx.globalAlpha = 1.0;
    }
    // Playhead at right edge
    if (audioEl && audioEl.duration > 0) {
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillRect(w - 2, 0, 2, h);
    }
  };

  useEffect(() => {
    let offsetA = 0;
    let offsetB = 0;
    let last = 0;
    const tick = (t) => {
      if (t - last > 33) {
        last = t;
        if (playingA) offsetA += 1;
        if (playingB) offsetB += 1;
        drawDeck(canvasA.current, colorA, playingA, offsetA, audioRefA?.current, tempoA, bpmA);
        drawDeck(canvasB.current, colorB, playingB, offsetB, audioRefB?.current, tempoB, bpmB);
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line
  }, [playingA, playingB, colorA, colorB, tempoA, tempoB, bpmA, bpmB]);

  // Size canvases to fit their container
  useEffect(() => {
    const resize = () => {
      [canvasA.current, canvasB.current].forEach((cv) => {
        if (!cv) return;
        const rect = cv.getBoundingClientRect();
        if (cv.width !== Math.floor(rect.width)) cv.width = Math.floor(rect.width);
        if (cv.height !== Math.floor(rect.height)) cv.height = Math.floor(rect.height);
      });
    };
    resize();
    window.addEventListener("resize", resize);
    const id = setInterval(resize, 400); // lazy but resilient
    return () => { window.removeEventListener("resize", resize); clearInterval(id); };
  }, []);

  return (
    <div data-testid="serato-waveforms" className="bg-[#05070B] border border-white/10 rounded-lg overflow-hidden flex flex-col">
      <div className="relative flex-1 min-h-[70px]">
        <canvas ref={canvasA} className="w-full h-full" />
      </div>
      <div className="h-[1px] bg-white/10" />
      <div className="relative flex-1 min-h-[70px]">
        <canvas ref={canvasB} className="w-full h-full" />
      </div>
    </div>
  );
}
